/**
 * Step 3: Your Calendars
 *
 * User selects which device calendars belong to them. The selection is
 * saved to the user profile and also drives the initial FimFam visibility
 * filter — only the picked calendars are shown in app by default.
 */

import { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getTypographyStyle } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';

type LinkCalendarsToSelfStepProps = {
  calendars: DeviceCalendar[];
  preSelectedIds: string[];
  onContinue: (selectedIds: string[]) => void;
  onBack: () => void;
};

export function LinkCalendarsToSelfStep({
  calendars,
  preSelectedIds,
  onContinue,
  onBack,
}: LinkCalendarsToSelfStepProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(preSelectedIds),
  );

  const toggleCalendar = (calendarId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(calendarId)) {
        next.delete(calendarId);
      } else {
        next.add(calendarId);
      }
      return next;
    });
  };

  const handleContinue = () => {
    if (selectedIds.size === 0) return;
    onContinue(Array.from(selectedIds));
  };

  const canProceed = selectedIds.size > 0;

  return (
    <View style={[styles.container, { backgroundColor: palette.background }]}>
      <View style={[styles.header, { borderBottomColor: palette.borderMuted }]}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={palette.text} />
        </TouchableOpacity>
        <Text style={[styles.title, { color: palette.text }]}>
          Your Calendars
        </Text>
      </View>

      <Text style={[styles.description, { color: palette.textMuted }]}>
        Select your calendars. These will be shown in FimFam and used to
        identify your events. You can show others later from the calendar
        menu.
      </Text>

      <ScrollView
        style={styles.calendarList}
        showsVerticalScrollIndicator={false}
      >
        {calendars.map((calendar) => {
          const isSelected = selectedIds.has(calendar.id);
          return (
            <TouchableOpacity
              key={calendar.id}
              style={[styles.calendarItem, { borderColor: palette.borderMuted }]}
              onPress={() => toggleCalendar(calendar.id)}
              activeOpacity={0.7}
            >
              <View style={styles.calendarItemLeft}>
                <View
                  style={[
                    styles.checkbox,
                    {
                      borderColor: isSelected
                        ? palette.primary
                        : palette.border,
                    },
                    isSelected && { backgroundColor: palette.primary },
                  ]}
                >
                  {isSelected && (
                    <Ionicons name="checkmark" size={16} color="#fff" />
                  )}
                </View>
                <View style={styles.calendarInfo}>
                  <Text style={[styles.calendarName, { color: palette.text }]}>
                    {calendar.title}
                    {calendar.isPrimary && (
                      <Text
                        style={[
                          styles.primaryBadge,
                          { color: palette.textMuted },
                        ]}
                      >
                        {' '}
                        (Primary)
                      </Text>
                    )}
                  </Text>
                  <Text
                    style={[
                      styles.calendarSource,
                      { color: palette.textMuted },
                    ]}
                  >
                    {calendar.source.name}
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {!canProceed && (
        <Text style={[styles.errorText, { color: palette.danger }]}>
          Select at least one calendar
        </Text>
      )}

      <View style={[styles.footer, { borderTopColor: palette.borderMuted }]}>
        <TouchableOpacity
          style={[
            styles.button,
            { backgroundColor: palette.primary },
            !canProceed && styles.buttonDisabled,
          ]}
          onPress={handleContinue}
          disabled={!canProceed}
        >
          <Text style={styles.buttonText}>Next</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const titleStyle = getTypographyStyle('title');
const bodyStyle = getTypographyStyle('body');
const subheadingStyle = getTypographyStyle('subheading');

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: Spacing.xl,
    borderBottomWidth: 1,
  },
  backButton: {
    marginRight: Spacing.lg,
  },
  title: {
    ...titleStyle,
    fontWeight: '700',
  },
  description: {
    ...bodyStyle,
    fontSize: 14,
    lineHeight: 20,
    padding: Spacing.xl,
    paddingTop: Spacing.md,
  },
  calendarList: {
    flex: 1,
    paddingHorizontal: Spacing.lg,
  },
  calendarItem: {
    borderWidth: 1,
    borderRadius: BorderRadius.sm,
    padding: Spacing.md,
    marginBottom: Spacing.sm,
  },
  calendarItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: BorderRadius.xs + 2,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  calendarInfo: {
    flex: 1,
  },
  calendarName: {
    ...subheadingStyle,
    fontSize: 14,
  },
  calendarSource: {
    ...bodyStyle,
    marginTop: Spacing.xxs,
  },
  primaryBadge: {
    ...bodyStyle,
    fontWeight: '400',
  },
  errorText: {
    ...bodyStyle,
    textAlign: 'center',
    marginTop: Spacing.sm,
    paddingHorizontal: Spacing.xl,
  },
  footer: {
    padding: Spacing.lg,
    borderTopWidth: 1,
  },
  button: {
    paddingVertical: Spacing.md + 2,
    borderRadius: BorderRadius.sm,
    alignItems: 'center',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonText: {
    ...subheadingStyle,
    color: '#FFFFFF',
  },
});
